import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { ThemeContext } from '../App';
import SearchBar from './Layout/SearchBar';
import Sidebar from './Layout/Sidebar';
import MainContent from './Layout/MainContent';
import ReportIssueModal from './Common/ReportIssueModal';
import { authorization, fetchOrUpdateIdToken } from '../auth/Authorization';
import { localConfig } from '../config/localConfig';
import { attachSession, isValidSessionError, handleSessionExpired } from '../utils/sessionManager';

// ========== LOCAL TESTING START ==========
import UnderwritingFeedback from './Underwriting/UnderwritingFeedback';
// ========== LOCAL TESTING END ==========

// Priority order for tabs: abcd, cam, model_tab, trigger_tab first, then the rest
const PRIORITY_TAB_ORDER = ['abcd', 'cam', 'model_tab', 'trigger_tab'];

/**
 * Reorders document types so that priority tabs (abcd, cam, model_tab, trigger_tab)
 * appear first in the specified order, followed by the remaining tabs in their
 * original order from the Lambda response.
 */
const reorderDocTypes = (docTypes) => {
  if (!Array.isArray(docTypes) || docTypes.length === 0) return docTypes;
  
  const priorityItems = [];
  const remainingItems = [];
  
  for (const docType of docTypes) {
    if (PRIORITY_TAB_ORDER.includes(docType)) {
      priorityItems.push(docType);
    } else {
      remainingItems.push(docType);
    }
  }
  
  // Sort priority items according to PRIORITY_TAB_ORDER
  priorityItems.sort((a, b) => PRIORITY_TAB_ORDER.indexOf(a) - PRIORITY_TAB_ORDER.indexOf(b));
  
  return [...priorityItems, ...remainingItems];
};

const Dashboard = ({ emailID, idToken, exp, source, hasAccess }) => {
  const [leadId, setLeadId] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const [tabsData, setTabsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [finReference, setFinReference] = useState('');
  const [displayTypes, setDisplayTypes] = useState({});
  const [applicantNames, setApplicantNames] = useState({});
  const [disclaimer, setDisclaimer] = useState('');
  const [urlEmailID, setUrlEmailID] = useState('');
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [viewedTabs, setViewedTabs] = useState(new Set());
  const [tabStartTime, setTabStartTime] = useState(null);
  const [tabOrder, setTabOrder] = useState([]);
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [pollingError, setPollingError] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [sequenceNumber, setSequenceNumber] = useState(0);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const { theme } = useContext(ThemeContext);

  // Ref to prevent duplicate polling
  const isPollingActiveRef = useRef(false);

  // ========== LOCAL TESTING START ==========
  // Load underwriting test data from sample outputs
  const loadUnderwritingTestData = useCallback((testLeadId) => {
    console.log('[LOCAL TEST] Loading underwriting test data for %s', testLeadId);
    

    try {
      // Map of available test files
      const testFiles = {
        'SCUBL00A0894': require('../sample outputs/SCUBL00A0894_20260115_115017.json'),
        // 'SCUBL00A3F33': require('../sample outputs/SCUBL00A3F33_20260115_105636.json'),
        'SCUBL00A8444': require('../sample outputs/SCUBL00A8444_20260114_144401 1.json'),
        'SCUBL00B82BE': require('../sample outputs/SCUBL00B82BE_20260115_113314.json'),
        'SCUBL00E4520': require('../sample outputs/SCUBL00E4520_20260115_112831.json'),
        'SCUBL0127F56': require('../sample outputs/new_frontend_UAToutput.json'),
        'SCUBL00A3F33': require('../sample outputs/test_uat_abcd.json'),
        // 'PLSA002B620C': require('../sample outputs/uat_backend_v2.json'),
        'PLSA002B620C': require('../sample outputs/uat_error_scenario.json'),
        // Perceptra tab-switcher test lead (BLSA000B5D3D)
        // NOTE: perceptraTabOutput.js is an ES module (`export default <string>`).
        // When loaded via CommonJS `require()` (as done here for all the other
        // .json test fixtures), Babel's ES-module interop means the returned
        // value is the *module object* `{ __esModule: true, default: "<html>..." }`,
        // NOT the raw HTML string itself. Every downstream check
        // (`typeof data === 'string'`) then fails, so the HTML is never
        // rendered via HtmlContent and the tab buttons never become
        // clickable. Unwrap `.default` here so `testData` is the actual
        // HTML string, matching the shape of every other test fixture.
        'BLSA000B5D3D': require('../sample outputs/perceptraTabOutput.js').default
      };
      
      const testData = testFiles[testLeadId];
      
      if (!testData) {
        setError(`No test data found for ${testLeadId}. Available test leads: ${Object.keys(testFiles).join(', ')}`);
        setLoading(false);
        return;
      }
      
      // Set up mock response with tabs including "Model Results" for testing
      const modelTabData = require('../sample outputs/modeltab_output.json');
      setTabsData({ 
        'AI-Based Credit Decisioning': testData,
        'model_tab': modelTabData
      });
      setActiveTab('AI-Based Credit Decisioning');
      setFinReference(testLeadId);
      setTabOrder(['AI-Based Credit Decisioning', 'model_tab']);
      setLoading(false);
      setError(null);
      
      console.log('[LOCAL TEST] ✅ Loaded test data for %s', testLeadId);
      
    } catch (error) {
      console.error('[LOCAL TEST] Failed to load test data:', error);
      setError(`Failed to load test data: ${error.message}`);
      setLoading(false);
    }
  }, []);
  // ========== LOCAL TESTING END ==========

  // Utility function to parse URL parameters
  const getUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      leadId: urlParams.get('leadId'),
      email: urlParams.get('email')
    };
  };
  const finalizeLogsForJob = async (jobId, leadId, userId, source) => {
  try {
    const app_name = localConfig.appName;
    const validatedIdToken = await fetchOrUpdateIdToken(
      sessionStorage.getItem("idToken"),
      sessionStorage.getItem("exp")
    );
    const access_Token = await authorization(validatedIdToken.idToken, app_name);
    
    await fetch(`${localConfig.apiBaseUrl}/finalize_logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': access_Token
      },
      body: JSON.stringify(attachSession({
        job_id: jobId,
        lead_id: leadId,
        user_id: userId,
        source: source
      }))
    });
    
    console.log('[FINALIZE] Logs finalized for job', jobId);
  } catch (err) {
    console.error('[FINALIZE] Failed to finalize logs:', err);
  }
};

  // Polling function for progressive document loading
  const startPolling = useCallback(async (jobId, totalDocs) => {
    const { lambdaUrl, apiKey, intervalMs, maxPolls } = localConfig.polling;
    
    let pollCount = 0;
    let previouslyRendered = new Set();
    
    console.log('[POLL_START] Starting polling for job %s, expecting %s documents', jobId, totalDocs);
    console.log('[POLL_CONFIG] Interval: %sms, Max polls: %s', intervalMs, maxPolls);
    
    const poll = async () => {
      pollCount++;
      console.log('[POLL] Attempt #%s/%s for job %s', pollCount, maxPolls, jobId);
      
      try {
        const response = await fetch(`${lambdaUrl}/${jobId}`, {
          headers: { 'x-api-key': apiKey }
        });
        
        console.log('[POLL] Lambda response status: %s', response.status);
        
        if (!response.ok) {
          console.error('[POLL] Lambda returned error status: %s', response.status);
          throw new Error(`Lambda polling failed: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`[POLL] Response data:`, data);
        
        // Check if Lambda returned an error
        if (data.error) {
          console.error('[POLL] Lambda returned error: %s', data.error);
          throw new Error(data.error);
        }
        
        // Debug: Check ordering data
        console.log('[DEBUG] requested_documents from Lambda:', data.requested_documents);
        console.log('[DEBUG] documents keys:', Object.keys(data.documents || {}));
        
        // Render ALL completed documents in correct order
        const newTabsData = {};
        const orderedDocTypes = reorderDocTypes(data.requested_documents || Object.keys(data.documents || {}));
        console.log('[DEBUG] Using order:', orderedDocTypes);
        
        for (const docType of orderedDocTypes) {
          const docData = data.documents?.[docType];
          if (docData && !previouslyRendered.has(docType)) {
            const displayName = docData.display_name || docType;
            
            // Add document with appropriate status
            if (docData.status === 'SUCCESS') {
              newTabsData[displayName] = docData.answer;
              console.log('[POLL] ✅ Document completed: %s (SUCCESS)', displayName);
            } else if (docData.status === 'NO_DATA') {
              newTabsData[displayName] = "NO_DATA";
              console.log('[POLL] ⚠️ Document completed: %s (NO_DATA)', displayName);
            } else if (docData.status === 'NOT_APPLICABLE') {
              newTabsData[displayName] = "NOT_APPLICABLE";
              console.log('[POLL] ℹ️ Document completed: %s (NOT_APPLICABLE)', displayName);
            } else {
              newTabsData[displayName] = "FAILURE";
              console.log('[POLL] ❌ Document completed: %s (FAILURE)', displayName);
            }
            
            // Extract applicant names if present
            console.log('[DEBUG] %s applicant_names:', displayName, docData.applicant_names);
            if (docData.applicant_names && Object.keys(docData.applicant_names).length > 0) {
              console.log('[DEBUG] Setting applicant names for %s:', displayName, docData.applicant_names);
              setApplicantNames(prev => ({...prev, ...docData.applicant_names}));
            } else {
              console.log('[DEBUG] No applicant_names for %s', displayName);
            }
            
            previouslyRendered.add(docType);
          }
        }
        
        // Update tabs progressively (don't auto-switch tabs)
        if (Object.keys(newTabsData).length > 0) {
          setTabsData(prev => {
            const updated = {...prev, ...newTabsData};
            
            // Rebuild tabOrder based on updated tabsData
            const orderedDisplayNames = orderedDocTypes
              .map(docType => data.documents?.[docType]?.display_name || docType)
              .filter(name => updated[name] !== undefined);
            
            setTabOrder(orderedDisplayNames);
            return updated;
          });
          
          // Don't auto-select any tab - let user choose
        }
        
        // Log progress
        console.log('[POLL] Progress: %s/%s (%s%%) - Status: %s', data.completed_documents, data.total_documents, data.progress_percentage, data.status);
        
        // Check completion first
        if (data.status === 'COMPLETED') {
          isPollingActiveRef.current = false;  // Reset polling flag
          setLoading(false);
          console.log('[POLL] ✅ All documents loaded successfully');
          finalizeLogsForJob(jobId, finReference, emailID, source);
          return;
        }
        
        // Check timeout
        if (pollCount >= maxPolls) {
          isPollingActiveRef.current = false;  // Reset polling flag
          setLoading(false);
          setTimeoutWarning(true);  // Show non-blocking warning
          // Auto-dismiss after 5 seconds
          setTimeout(() => setTimeoutWarning(false), 5000);
          console.warn('[POLL] ⚠️ Timeout reached after %s polls', pollCount);
          finalizeLogsForJob(jobId, finReference, emailID, source);
          return;
        }
        
        // Continue polling
        console.log('[POLL] Scheduling next poll in %sms...', intervalMs);
        setTimeout(poll, intervalMs);
        
      } catch (error) {
        console.error('[POLL] ❌ Error on poll #%s:', pollCount, error);
        
        // Retry on error if under max polls
        if (pollCount < maxPolls) {
          console.log('[POLL] Retrying in %sms...', intervalMs);
          setTimeout(poll, intervalMs);
        } else {
          isPollingActiveRef.current = false;  // Reset polling flag
          setLoading(false);
          setPollingError(true);  // Show non-blocking error toast
          setTimeout(() => setPollingError(false), 8000);  // Auto-dismiss after 8 seconds
          console.error('[POLL] ❌ Giving up after %s failed attempts', pollCount);
          finalizeLogsForJob(jobId, finReference, emailID, source);
        }
      }
    };
    
    poll();
  }, [activeTab, localConfig.polling]);

  const handleSearch = useCallback(async (searchLeadId = leadId) => {
    if (!searchLeadId.trim()) {
      setError('Please enter a Lead ID');
      return;
    }

    // Guard: Prevent duplicate search while polling is active
    if (isPollingActiveRef.current) {
      // console.log('[SEARCH] ⚠️ Polling already active, ignoring duplicate request for %s', searchLeadId);
      return;
    }

    // Check for testing pattern: <LEAD_ID>_ABCDtesting
    if (searchLeadId.includes('_ABCDtesting')) {
      const testLeadId = searchLeadId.split('_ABCDtesting')[0];
      loadUnderwritingTestData(testLeadId);
      return;
    }

    setLoading(true);
    setError(null);
    setTabsData({});
    setActiveTab(null);
    setViewedTabs(new Set());
    setTabOrder([]);  // Reset tab order on new search
    setTimeoutWarning(false);  // Reset timeout warning

    try {
      const app_name = localConfig.appName;
      const validatedIdToken = await fetchOrUpdateIdToken(idToken, exp);
      const authResult = await authorization(validatedIdToken.idToken, app_name);

      // Check if user has access to the application
      if (!authResult.hasAccess) {
        setError('Access Denied: You do not have permission to access this application.');
        return;
      }

      const requestData = attachSession({
        query: searchLeadId,
        user: urlEmailID || emailID || localConfig.defaultEmail,
        source: source || "system",
      });

      const apiUrl = `${localConfig.apiBaseUrl}/invocations`;
      // const apiUrl = `${localConfig.apiBaseUrl}/internal/invocations`;
      // const apiUrl = `${localConfig.baseUrl}/invocations`;
      
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authResult.accessToken,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        // Force-logout if this session was invalidated by a newer sign-in.
        if (await isValidSessionError(response)) {
          handleSessionExpired();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Check if this is a polling response (new flow)
      if (responseData.job_id && responseData.status === 'PROCESSING') {
        console.log('[JOB] Created job %s for %s', responseData.job_id, searchLeadId);
        setFinReference(responseData.fin_reference || searchLeadId);
        setTotalDocuments(responseData.total_documents);
        setJobId(responseData.job_id);  // Capture job_id for analytics
        setSequenceNumber(0);  // Reset sequence number
        
        // Set polling flag before starting
        isPollingActiveRef.current = true;
        
        // Start polling for progressive document loading
        startPolling(responseData.job_id, responseData.total_documents);
        return;
      }
      
      // Handle different response formats (old flow - backward compatible)
      if (responseData.Response && typeof responseData.Response === "object" && !Array.isArray(responseData.Response)) {
        
        setTabsData(responseData.Response);
        setFinReference(responseData.Parameters?.finReference || searchLeadId);
        
        // Handle DisplayTypes if provided
        if (responseData.DisplayTypes && typeof responseData.DisplayTypes === "object") {
          setDisplayTypes(responseData.DisplayTypes);
        } else {
          setDisplayTypes({});
        }
        
        // Handle ApplicantNames if provided
        if (responseData.ApplicantNames && typeof responseData.ApplicantNames === "object") {
          setApplicantNames(responseData.ApplicantNames);
        } else {
          setApplicantNames({});
        }
        
        // Handle Disclaimer if provided
        if (responseData.Disclaimer && typeof responseData.Disclaimer === "string") {
          setDisclaimer(responseData.Disclaimer);
        } else {
          setDisclaimer('');
        }
        
        // Set first available non-disabled tab as active
        const availableTabs = Object.keys(responseData.Response);
        const firstEnabledTab = availableTabs.find(tab => responseData.Response[tab] !== "FAILURE");
        if (firstEnabledTab) {
          setActiveTab(firstEnabledTab);
        } else if (availableTabs.length > 0) {
          // If all tabs are disabled, still set the first one as active but it won't be clickable
          setActiveTab(availableTabs[0]);
        }
        
        // Old flow complete - stop loading
        setLoading(false);
        
      } else if (typeof responseData.Response === "string" && responseData.Response.trim() !== "") {
        setError(responseData.Response);
        setLoading(false);
      } else if (responseData.Response === "") {
        setError('No data found for this Lead ID. Please check the Lead ID and try again.');
        setLoading(false);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError('Failed to fetch data. Please try again.' || error.message);
      setLoading(false);
    }
  }, [leadId, idToken, exp, emailID, urlEmailID]);

  const handleTabChange = (tabKey) => {
    const currentTime = Date.now();
    const timestamp = new Date().toISOString();
    const currentSeq = sequenceNumber;  // ✅ Capture sequence number to avoid React batching issues
    let eventsCount = 0;  // ✅ Track actual events sent to avoid sequence gaps
    
    // ========== EVENT 1: TAB_EXIT (for previous tab) ==========
    if (activeTab && tabStartTime && jobId) {
      const timeSpent = currentTime - tabStartTime;
      console.log('[ANALYTICS] Exiting %s: %sms (seq=%s)', activeTab, timeSpent, currentSeq + eventsCount);
      
      // Send TAB_EXIT event
      fetch(`${localConfig.apiBaseUrl}/analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'TAB_EXIT',
          tab: activeTab,
          timestamp: timestamp,
          time_spent_ms: timeSpent,
          sequence_number: currentSeq + eventsCount,  // ✅ Use captured value + events sent so far
          job_id: jobId,
          lead_id: finReference,
          user_id: emailID,
          next_tab: tabKey
        })
      }).catch(err => console.error('[ANALYTICS] Failed to send TAB_EXIT:', err));
      
      eventsCount++;  // ✅ Increment local counter
    }
    
    // ========== EVENT 2: TAB_ENTRY (for new tab - ALWAYS) ==========
    if (jobId) {
      console.log('[ANALYTICS] Entering %s (seq=%s)', tabKey, currentSeq + eventsCount);
      
      // Send TAB_ENTRY event
      fetch(`${localConfig.apiBaseUrl}/analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'TAB_ENTRY',
          tab: tabKey,
          timestamp: timestamp,
          sequence_number: currentSeq + eventsCount,  // ✅ Use captured value + events sent so far
          job_id: jobId,
          lead_id: finReference,
          user_id: emailID,
          previous_tab: activeTab || null,
          is_first_view: !viewedTabs.has(tabKey)
        })
      }).catch(err => console.error('[ANALYTICS] Failed to send TAB_ENTRY:', err));
      
      eventsCount++;  // ✅ Increment local counter
    }
    
    // ========== STATE UPDATES ==========
    setSequenceNumber(prev => prev + eventsCount);  // ✅ Increment by actual events sent (1 or 2)
    setActiveTab(tabKey);
    setTabStartTime(currentTime);
    setViewedTabs(prev => new Set([...prev, tabKey]));
  };

  // URL Parameters and PostMessage handler
  useEffect(() => {
    // Check if app is embedded in iframe
    const embedded = window.self !== window.top;
    setIsEmbedded(embedded);
    
    // First, check for URL parameters
    const urlParams = getUrlParameters();
    
    if (urlParams.leadId) {
      setLeadId(urlParams.leadId);
      
      // Set email from URL if provided
      if (urlParams.email) {
        setUrlEmailID(urlParams.email);
      }
      
      // Auto-trigger search with URL lead ID
      handleSearch(urlParams.leadId);
      return; // Exit early if URL params are found
    }
    
    // Fallback to PostMessage listener for embedded mode
    if (embedded) {
      
      const handleMessage = (event) => {
        // Validate message origin to prevent cross-origin attacks
        const allowedOrigins = [
          window.location.origin,
          localConfig.parentOrigin,
          'https://piramalfinance.com',
          'https://www.piramalfinance.com'
        ].filter(Boolean);
        
        if (!allowedOrigins.includes(event.origin)) {
          console.warn('[POST_MESSAGE] Blocked message from untrusted origin: %s', event.origin);
          return;
        }
        
        if (event.data && event.data.leadId && event.data.email) {
          setLeadId(event.data.leadId);
          setUrlEmailID(event.data.email);
          // Auto-trigger search when lead ID is received
          handleSearch(event.data.leadId);
        }
      };
      
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [handleSearch]);

  return (
    <div className="dashboard">
      {/* Header with integrated Search Bar - Hidden when embedded */}
      {!isEmbedded && (
        <div className="dashboard-header">
          <div className="header-left">
            <img
              src={require('../Piramal-Finance-Logo-removebg-preview.png')}
              alt="Piramal Finance"
            />
          </div>
          
          <div className="header-center">
            <SearchBar 
              leadId={leadId}
              setLeadId={setLeadId}
              onSearch={handleSearch}
              loading={loading}
            />
          </div>
          
          <div className="header-right">
            <div className="user-info">
              <span>{emailID}</span>
              <div className="user-avatar">
                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.4853 3.51473C18.2188 1.24823 15.2053 0 12 0C8.79469 0 5.78123 1.24823 3.51469 3.51473C1.24823 5.78123 0 8.79469 0 12C0 15.2053 1.24823 18.2188 3.51469 20.4853C5.78123 22.7518 8.79469 24 12 24C15.2053 24 18.2188 22.7518 20.4853 20.4853C22.7518 18.2188 24 15.2053 24 12C24 8.79469 22.7518 5.78123 20.4853 3.51473ZM12 22.5938C8.86552 22.5938 6.04514 21.2249 4.10381 19.0542C5.30733 15.8635 8.38819 13.5938 12 13.5938C9.67003 13.5938 7.78125 11.705 7.78125 9.375C7.78125 7.04503 9.67003 5.15625 12 5.15625C14.33 5.15625 16.2188 7.04503 16.2188 9.375C16.2188 11.705 14.33 13.5938 12 13.5938C15.6118 13.5938 18.6927 15.8635 19.8962 19.0542C17.9549 21.2249 15.1345 22.5938 12 22.5938Z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="main-content" style={{ position: 'relative' }}>
        {/* Timeout Warning Toast */}
        {timeoutWarning && (
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '400px'
          }}>
            <svg style={{width: '20px', height: '20px', flexShrink: 0}} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <span>Polling timeout - some documents may still be processing</span>
            <button 
              onClick={() => setTimeoutWarning(false)}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#92400e',
                padding: '0 4px'
              }}
            >×</button>
          </div>
        )}

        {/* Polling Error Toast */}
        {pollingError && (
          <div style={{
            position: 'absolute',
            top: '80px',
            right: '20px',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '400px'
          }}>
            <svg style={{width: '20px', height: '20px', flexShrink: 0}} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <span>Failed to load some documents - viewing available data</span>
            <button 
              onClick={() => setPollingError(false)}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#991b1b',
                padding: '0 4px'
              }}
            >×</button>
          </div>
        )}

        {/* Report Issue Modal with integrated pull-tab */}
        <ReportIssueModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onOpen={() => setIsReportModalOpen(true)}
          emailID={emailID}
          leadId={finReference}
          jobId={jobId}
          source={source}
          availableTabs={tabOrder.length > 0 ? tabOrder : Object.keys(tabsData)}
        />

        {/* Sidebar */}
        <Sidebar 
          tabs={tabOrder.length > 0 ? tabOrder : Object.keys(tabsData)}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          loading={loading}
          tabsData={tabsData}
          totalDocuments={totalDocuments}
          viewedTabs={viewedTabs}
        />

        {/* ========== LOCAL TESTING START ========== */}
        {/* Conditional rendering for ABCD test tab */}
        {activeTab === 'AI-Based Credit Decisioning' && tabsData['AI-Based Credit Decisioning'] ? (
          <UnderwritingFeedback 
            data={tabsData['AI-Based Credit Decisioning']} 
            emailID={emailID}
            jobId={jobId}
            source={source}
          />
        ) : (
          /* ========== LOCAL TESTING END ========== */
          <MainContent 
            activeTab={activeTab}
            tabsData={tabsData}
            finReference={finReference}
            emailID={emailID}
            loading={loading}
            error={error}
            displayTypes={displayTypes}
            applicantNames={applicantNames}
            disclaimer={disclaimer}
            source={source}
            idToken={idToken}
            exp={exp}
          />
          /* ========== LOCAL TESTING START ========== */
        )}
        {/* ========== LOCAL TESTING END ========== */}
      </div>
    </div>
  );
};

export default Dashboard;